import { v4 as uuid } from 'uuid';
import {
  type DescriptionFieldSetting,
  FieldSettingType,
  type LocationFieldSetting,
  type LocationTemplate,
  type SubjectTemplate,
  type SubjectTemplateFieldSetting,
} from '@timeedit/calendar-integration-api-contract';
import type { TField, TType } from '@timeedit/types';

const SPACE_SYMBOL = '␣';

const formatSpaces = (count: number): string => {
  if (count === 0) return '';
  if (count <= 3) {
    return Array(count).fill(SPACE_SYMBOL).join(' ');
  }
  return `${SPACE_SYMBOL} x${count}`;
};

/**
 * Formats text for display, making whitespace visible where needed
 * - Whitespace-only: Show ␣ symbols (separated by one space for clarity)
 * - Leading/trailing whitespace: Show ␣ symbols (separated by one space for clarity)
 * - Interior whitespace: Keep as-is
 */
export const getWhitespaceLabel = (text: string): string => {
  const trimmed = text.trim();

  if (trimmed === '') {
    return formatSpaces(text.length);
  }

  const leadingCount = text.length - text.trimStart().length;
  const trailingCount = text.length - text.trimEnd().length;

  if (leadingCount === 0 && trailingCount === 0) {
    return text;
  }

  return `${formatSpaces(leadingCount)}${trimmed}${formatSpaces(
    trailingCount
  )}`;
};

export type SubjectTemplateRow = {
  id: string;
  fields: SubjectTemplateFieldSetting[];
};

export type LocationTemplateRow = {
  id: string;
  fields: LocationFieldSetting[];
};

type TemplateWithFields<F> = { fields: F[] };
type RowWithFields<F> = { id: string; fields: F[] };

const templatesToRows = <F extends { id?: string }>(
  templates: TemplateWithFields<F>[]
): RowWithFields<F>[] => {
  return templates.map((template, index) => ({
    id: template.fields[0]?.id ?? `template-${index}-${uuid()}`,
    fields: template.fields,
  }));
};

const rowsToTemplates = <F>(
  rows: RowWithFields<F>[]
): TemplateWithFields<F>[] => {
  return rows.map((row) => ({ fields: row.fields }));
};

export const subjectTemplatesToRows = (templates: SubjectTemplate[]) =>
  templatesToRows<SubjectTemplateFieldSetting>(templates);

export const subjectRowsToTemplates = (rows: SubjectTemplateRow[]) =>
  rowsToTemplates<SubjectTemplateFieldSetting>(rows);

export const locationTemplatesToRows = (templates: LocationTemplate[]) =>
  templatesToRows<LocationFieldSetting>(templates);

export const locationRowsToTemplates = (rows: LocationTemplateRow[]) =>
  rowsToTemplates<LocationFieldSetting>(rows);

export type FieldDisplayInfo = {
  label: string;
  tooltip?: string;
  color: 'default' | 'green' | 'purple' | 'orange';
};

export const getFieldDisplayInfo = (
  fieldSetting:
    | SubjectTemplateFieldSetting
    | DescriptionFieldSetting
    | LocationFieldSetting,
  field?: TField,
  objectType?: TType
): FieldDisplayInfo => {
  switch (fieldSetting.type) {
    case FieldSettingType.objectField: {
      const typeName = objectType?.name ?? 'Unknown';
      const fieldName = field?.name ?? `Field ${fieldSetting.fieldId}`;
      return {
        label: `${typeName}.${fieldName}`,
        tooltip: field?.extId,
        color: 'green',
      };
    }
    case FieldSettingType.reservationField: {
      const fieldName = field?.name ?? `Field ${fieldSetting.fieldId}`;
      return {
        label: fieldName,
        tooltip: field?.extId,
        color: 'purple',
      };
    }
    case FieldSettingType.reservationInfo:
      return {
        label: fieldSetting.propertyName,
        color: 'orange',
      };
    case FieldSettingType.freeText: {
      return {
        label: getWhitespaceLabel(fieldSetting.text),
        color: 'default',
      };
    }
    default:
      return {
        label: 'Unknown',
        color: 'default',
      };
  }
};
